'use client';

import { useEffect, useRef } from 'react';
import { createNoise3D } from 'simplex-noise';
import styles from './animation.module.scss';

export interface IWaveColor {
  r: number,
  g: number,
  b: number,
  a: number,
}

export interface IAnimationParams {
  factor: number,
  variation: number,
  amplitude: number,
  lines: number,
  waveColor: IWaveColor,
  lineStroke: number,
  speed: number,
}

export interface IAnimationProps {
  parameters?: IAnimationParams,
}

export const DEFAULT_PARAMS: IAnimationParams = {
  factor: 0.02,
  variation: 0.0017,
  amplitude: 150,
  lines: 24,
  waveColor: { r: 255, g: 255, b: 255, a: 0.1 },
  lineStroke: 1,
  speed: 0.0005,
};

const MAX_PIXEL_RATIO = 0.9;

/**
 * Animated noise waves drawn on a canvas that fills the positioned parent.
 * `parameters` are read once, on mount.
 */
const Animation = ({ parameters = DEFAULT_PARAMS }: IAnimationProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return undefined;
    }

    const noise3D = createNoise3D();
    const { lines, factor, variation, amplitude, speed, lineStroke, waveColor } = parameters;
    let randomness = Array.from({ length: lines }, (_, index) => index * factor);
    let size = { width: 0, height: 0 };
    let requestId = 0;

    const syncSize = (): void => {
      const { width, height } = canvas.getBoundingClientRect();

      if (!width || !height) {
        return;
      }

      const pixelRatio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);

      size = { width, height };
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      context.scale(pixelRatio, pixelRatio);
    };

    const draw = (): void => {
      context.clearRect(0, 0, size.width, size.height);
      context.lineWidth = lineStroke;

      randomness = randomness.map((offset) => {
        let randomY = 0;

        context.beginPath();
        context.moveTo(0, size.height / 2);
        for (let x = 0; x <= size.width; x++) {
          randomY = noise3D(x * variation + offset, x * variation, 1);
          context.lineTo(x, size.height / 2 + (amplitude + Math.random() * 0.005) * randomY);
        }

        const alpha = Math.min(Math.abs(randomY) + 0.001, waveColor.a);

        context.strokeStyle = `rgba(${ waveColor.r }, ${ waveColor.g }, ${ waveColor.b },${ alpha * 2 })`;
        context.stroke();
        context.closePath();

        return offset + speed;
      });

      requestId = window.requestAnimationFrame(draw);
    };

    syncSize();
    const resizeObserver = new ResizeObserver(syncSize);
    resizeObserver.observe(canvas);
    requestId = window.requestAnimationFrame(draw);

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(requestId);
    };
  }, []);

  return <canvas id="webgl" ref={ canvasRef } className={ styles.canvas } />;
};

export default Animation;
